import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUsageVisibility } from '../hooks/useUsageVisibility';
import ProfileImage from '../components/ProfileImage';
import NotificationsBell from '../components/NotificationsBell';
import MobileNotifications from '../components/MobileNotifications';
import db from '../utils/firestore';
import SeoHead from '../components/SeoHead';
import { MISSION_SEO } from '../config/missionSeo';
import { useBookOrDesktop } from '../hooks/useBookOrDesktop';
// PlatformMessageService, getAuth, openConstructionNotice imports removed with PromptBar
import GlobalFooter from '../components/GlobalFooter';
import Modal from '../components/common/Modal';
import StoryFeatureBlock from '../components/StoryFeatureBlock';
import { BRAND_MARK_ON_DARK_SRC } from '../config/brandMark.generated';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeInLeft = keyframes`
  from { opacity: 0; transform: translateX(-30px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeInRight = keyframes`
  from { opacity: 0; transform: translateX(30px); }
  to { opacity: 1; transform: translateX(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.02); }
  100% { transform: scale(1); }
`;

// Header for navigation
const HomeHeader = styled.header`
  position: fixed;
  top: 1rem;
  right: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 100;
  background: rgba(255, 255, 255, 0.9);
  padding: 0.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  @media (max-width: 768px) {
    display: none; // Hide on mobile as we'll use the mobile menu instead
  }
`;

const HeaderIconLink = styled(Link)`
  color: var(--brand-text-secondary);
  text-decoration: none;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;

  &:hover {
    color: var(--brand-charcoal);
    background-color: rgba(0, 0, 0, 0.03);
  }

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
    margin-right: 0.5rem;
  }

  &:hover svg {
    opacity: 1;
  }

  &.active {
    color: var(--brand-gold);
    background-color: rgba(198, 167, 94, 0.08);

    svg {
      opacity: 1;
      color: var(--brand-gold);
    }
  }
`;

const HeaderIconButton = styled.button`
  color: var(--brand-text-secondary);
  text-decoration: none;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 8px;
  background: none;
  border: none;
  font-family: inherit;
  font-size: inherit;
  cursor: pointer;

  &:hover {
    color: var(--brand-charcoal);
    background-color: rgba(0, 0, 0, 0.03);
  }

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
    margin-right: 0.5rem;
  }

  &:hover svg {
    opacity: 1;
  }
`;

// Mobile menu components
const MobileMenuToggle = styled.button`
  display: none;
  position: fixed;
  top: 1rem;
  right: 1rem;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 8px;
  z-index: 1100;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 5px;
  padding: 10px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    display: flex;
  }

  @media (max-width: 480px) {
    width: 38px;
    height: 38px;
    top: 0.75rem;
    right: 0.75rem;
  }
`;

const MobileMenuIcon = styled.div<{ isOpen: boolean }>`
  width: 100%;
  height: 100%;
  position: relative;

  span {
    display: block;
    position: absolute;
    height: 2px;
    width: 100%;
    background: var(--brand-black);
    border-radius: 2px;
    opacity: 1;
    left: 0;
    transform: rotate(0deg);
    transition: .25s ease-in-out;

    &:nth-child(1) {
      top: ${props => props.isOpen ? '9px' : '4px'};
      transform: ${props => props.isOpen ? 'rotate(45deg)' : 'rotate(0)'};
    }

    &:nth-child(2) {
      top: 9px;
      opacity: ${props => props.isOpen ? '0' : '1'};
    }

    &:nth-child(3) {
      top: ${props => props.isOpen ? '9px' : '14px'};
      transform: ${props => props.isOpen ? 'rotate(-45deg)' : 'rotate(0)'};
    }
  }
`;

const MobileMenuDropdown = styled.div<{ isOpen: boolean }>`
  display: none;
  position: fixed;
  top: 4rem;
  right: 1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  padding: 1rem 0;
  z-index: 200000; /* above prompt bars */
  flex-direction: column;
  width: 200px;
  transform: ${props => props.isOpen ? 'scale(1)' : 'scale(0.95)'};
  opacity: ${props => props.isOpen ? '1' : '0'};
  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
  transform-origin: top right;
  transition: all 0.25s ease-out;

  @media (max-width: 768px) {
    display: flex;
  }

  @media (max-width: 480px) {
    width: 180px;
    top: 3.5rem;
    right: 0.75rem;
    padding: 0.75rem 0;
  }
`;

const MobileMenuLink = styled(Link)`
  color: var(--brand-text-secondary);
  text-decoration: none;
  padding: 0.85rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.2s ease;
  font-size: 0.95rem;

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #000;

    svg {
      opacity: 1;
    }
  }

  @media (max-width: 480px) {
    padding: 0.75rem 1.25rem;
    font-size: 0.9rem;

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const MobileMenuButton = styled.button`
  color: var(--brand-text-secondary);
  text-decoration: none;
  padding: 0.85rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.2s ease;
  background: transparent;
  border: none;
  width: 100%;
  text-align: left;
  font-family: inherit;
  font-size: 0.95rem;
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #000;

    svg {
      opacity: 1;
    }
  }

  @media (max-width: 480px) {
    padding: 0.75rem 1.25rem;
    font-size: 0.9rem;

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const PageContainer = styled.div`
  padding: 6.5rem 2.5rem 0;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  background-color: var(--brand-canvas);
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 5.5rem 1.5rem 0;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    padding: 4.75rem 1rem 0;
  }
`;

// ── Hero ──────────────────────────────────────────────────────────────────────

const MissionHero = styled.div`
  background: linear-gradient(135deg, var(--brand-black) 0%, var(--brand-gold) 100%);
  color: white;
  padding: 5rem 4rem;
  border-radius: 20px;
  margin-bottom: 5.5rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(26, 26, 26, 0.25);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: ${fadeIn} 0.8s ease-out;
  width: 100%;

  @media (max-width: 768px) {
    padding: 3.5rem 2rem;
    margin-bottom: 4rem;
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    padding: 2.5rem 1rem;
    margin-bottom: 3rem;
    border-radius: 12px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.07' fill-rule='evenodd'/%3E%3C/svg%3E") repeat;
    opacity: 0.3;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 70%;
    background: linear-gradient(to top, rgba(26, 26, 26, 0.2) 0%, transparent 100%);
    z-index: 1;
  }
`;

const HeroLabel = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: lowercase;
  color: var(--brand-gold);
  margin-bottom: 1.25rem;
  position: relative;
  z-index: 2;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const HeroTitle = styled.h1`
  font-size: 3.2rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  text-wrap: balance;
  line-height: 1.15;
  position: relative;
  z-index: 2;
  letter-spacing: -0.03em;
  max-width: 800px;
  font-family: 'Space Grotesk', sans-serif;

  @media (max-width: 768px) {
    font-size: 2.4rem;
    line-height: 1.2;
  }

  @media (max-width: 480px) {
    font-size: 1.85rem;
    line-height: 1.25;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  font-weight: 400;
  max-width: 600px;
  text-wrap: balance;
  position: relative;
  z-index: 2;
  opacity: 0.8;
  line-height: 1.55;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

// ── Quote ─────────────────────────────────────────────────────────────────────

const QuoteContainer = styled.div`
  margin: 0 auto 5.5rem;
  padding: 2.5rem 3rem;
  max-width: 780px;
  width: 100%;
  text-align: center;
  box-sizing: border-box;
  position: relative;
  animation: ${fadeIn} 0.8s ease-out 0.2s both;

  &::before,
  &::after {
    content: '';
    display: block;
    height: 2px;
    background: linear-gradient(90deg, transparent, var(--brand-gold), transparent);
    margin: 0 auto;
    width: 60%;
  }

  &::before { margin-bottom: 2rem; }
  &::after  { margin-top: 2rem; }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    margin-bottom: 4rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 0.75rem;
    margin-bottom: 3.25rem;
  }
`;

const QuoteText = styled.p`
  font-size: 1.5rem;
  font-weight: 300;
  font-style: italic;
  line-height: 1.65;
  color: var(--brand-black);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  text-wrap: balance;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1.1rem;
    line-height: 1.6;
  }
`;

// ── Story Blocks ──────────────────────────────────────────────────────────────

// ── Timeline ──────────────────────────────────────────────────────────────────

const TimelineSection = styled.div`
  margin-bottom: 6rem;
  animation: ${fadeIn} 0.7s ease-out 0.1s both;
`;

const TimelineSectionLabel = styled.span`
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: lowercase;
  color: var(--brand-gold);
  margin-bottom: 0.75rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const TimelineSectionHeading = styled.h2`
  font-size: 2.2rem;
  font-weight: 700;
  color: var(--brand-black);
  margin-bottom: 3rem;
  line-height: 1.2;
  letter-spacing: -0.02em;
  font-family: 'Space Grotesk', sans-serif;
  text-wrap: balance;

  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin-bottom: 2.25rem;
  }
`;

const TimelineWrapper = styled.div`
  position: relative;
  padding-left: 0;

  &::before {
    content: '';
    position: absolute;
    left: 31px;
    top: 8px;
    bottom: 8px;
    width: 2px;
    background: linear-gradient(
      to bottom,
      rgba(198, 167, 94, 0.15),
      rgba(198, 167, 94, 0.6) 20%,
      rgba(198, 167, 94, 0.6) 80%,
      rgba(198, 167, 94, 0.15)
    );

    @media (max-width: 480px) {
      left: 23px;
    }
  }
`;

const TimelineItem = styled.div<{ delay?: string }>`
  display: flex;
  align-items: flex-start;
  gap: 1.75rem;
  margin-bottom: 2.75rem;
  animation: ${fadeInLeft} 0.6s ease-out ${props => props.delay || '0s'} both;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 480px) {
    gap: 1.25rem;
    margin-bottom: 2.25rem;
  }
`;

const TimelineIconBox = styled.div<{ gradient: string }>`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${props => props.gradient};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);

  svg {
    width: 26px;
    height: 26px;
    color: white;
    opacity: 0.9;
  }

  @media (max-width: 480px) {
    width: 48px;
    height: 48px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const TimelineContent = styled.div`
  padding-top: 0.5rem;
  flex: 1;
  min-width: 0;

  @media (max-width: 480px) {
    padding-top: 0.25rem;
  }
`;

const TimelineEra = styled.span`
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: lowercase;
  color: var(--brand-gold);
  margin-bottom: 0.3rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const TimelineTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--brand-black);
  margin-bottom: 0.4rem;
  letter-spacing: -0.01em;
  font-family: 'Space Grotesk', sans-serif;

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const TimelineDesc = styled.p`
  font-size: 0.98rem;
  line-height: 1.65;
  color: var(--brand-text-secondary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;

  @media (max-width: 480px) {
    font-size: 0.93rem;
  }
`;

// ── CTA ───────────────────────────────────────────────────────────────────────

const CTASection = styled.div`
  margin: 4rem auto;
  text-align: center;
  padding: 3.5rem 3rem;
  background: linear-gradient(135deg, rgba(198, 167, 94, 0.07) 0%, rgba(26, 26, 26, 0.03) 100%);
  border-radius: 20px;
  animation: ${pulse} 4s infinite ease-in-out;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    margin: 3rem auto;
    padding: 2.5rem 1.5rem;
    border-radius: 16px;
  }

  @media (max-width: 480px) {
    margin: 2.5rem auto;
    padding: 2rem 1rem;
    border-radius: 14px;
  }
`;

const CTATitle = styled.h2`
  font-size: 2.4rem;
  margin-bottom: 0.75rem;
  color: var(--brand-black);
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 700;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.7rem;
  }
`;

const CTASubtext = styled.p`
  font-size: 1.05rem;
  color: var(--brand-text-secondary);
  margin-bottom: 2rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  @media (max-width: 480px) {
    font-size: 0.98rem;
    margin-bottom: 1.5rem;
  }
`;

const CTAButton = styled.button`
  display: inline-block;
  background: linear-gradient(90deg, var(--brand-gold), var(--brand-gold-hover));
  color: white;
  padding: 1rem 2.25rem;
  border-radius: 30px;
  font-size: 1.1rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(198, 167, 94, 0.3);
  font-family: 'Space Grotesk', sans-serif;
  letter-spacing: -0.005em;

  @media (max-width: 768px) {
    padding: 0.9rem 1.8rem;
    font-size: 1.05rem;
  }

  @media (max-width: 480px) {
    padding: 0.8rem 1.6rem;
    font-size: 1rem;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(198, 167, 94, 0.4);
    background: linear-gradient(90deg, var(--brand-gold-hover), var(--brand-gold));
  }

  &:active {
    transform: translateY(0);
  }
`;

// ── Bottom CTA ────────────────────────────────────────────────────────────────

// DarkCTAContainer and DarkCTAWrap replaced with inline styles (matches home page exactly)

const DarkCTATitle = styled.h2`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(1.2rem, 5.5vw, 3.75rem);
  font-weight: 700;
  color: #fff;
  margin-bottom: 3rem;
  letter-spacing: -0.02em;
  text-transform: none;
  text-align: center;
  line-height: 1.3;
  width: 100%;
  text-wrap: balance;

  @media (min-width: 500px) {
    white-space: nowrap;
  }
`;

const DarkCTAGradientText = styled.span`
  background: linear-gradient(135deg, var(--brand-gold-alt), #B8924C);
  background-size: 200% 100%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const DarkCTALogo = styled.img`
  display: inline-block;
  vertical-align: baseline;
  object-fit: contain;
  height: 1.02em; /* Taller to match capital O/P with built-in image padding */
  margin-left: -0.16em; /* Tighter pull to compress image padding next to 't' */
  margin-right: -0.14em; /* Tighter pull next to 'n' */
  margin-bottom: -0.08em; /* Deeper baseline alignment for taller element */
  filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.15)) drop-shadow(0 0 4px rgba(255, 255, 255, 0.18)); /* Very subtle white shade for dark background contrast */
`;

// DarkCTAButton replaced with inline styles (matches home page exactly)

const ClosingQuoteContainer = styled.div`
  margin: 0 auto 5rem;
  padding: 2.5rem 3rem;
  max-width: 820px;
  width: 100%;
  text-align: center;
  box-sizing: border-box;
  border-top: 2px solid rgba(198, 167, 94, 0.4);
  border-bottom: 2px solid rgba(198, 167, 94, 0.4);
  animation: ${fadeIn} 0.8s ease-out both;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    margin: 0 auto 3.75rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 0.75rem;
    margin: 0 auto 3rem;
  }
`;

const ClosingQuoteText = styled.p`
  font-size: 1.45rem;
  font-weight: 300;
  font-style: italic;
  text-wrap: balance;
  line-height: 1.65;
  color: var(--brand-black);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.05rem;
  }
`;

// ── Component ─────────────────────────────────────────────────────────────────

const Mission: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated, logout } = useAuth();
  const { canSeeWallet, canSeeActivity } = useUsageVisibility();
  const handleLogin = () => {
    navigate('/auth', { state: { returnUrl: '/mission' } });
  };

  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  // Signed in: the desktop; anyone else: the calendar. One rule, shared with the rebuild (src/hooks/useBookOrDesktop.ts).
  const openBookingModal = React.useCallback(() => setIsBookingModalOpen(true), []);
  const handleJoinForFree = useBookOrDesktop(openBookingModal);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node) &&
          !(event.target as Element).closest('button[data-mobile-toggle="true"]')) {
        setMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
    <PageContainer>
      {/* One copy of what this page tells search, shared with its rebuild in the new design. */}
      <SeoHead {...MISSION_SEO} />
      <HomeHeader>
        
        <HeaderIconLink to="/team">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          team
        </HeaderIconLink>
        <HeaderIconLink to="/insights">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="2" x2="12" y2="6" />
            <path d="M5 10a7 7 0 0 1 14 0c0 3.07-1.63 4.68-3 5.58a2 2 0 0 0-1 1.7V19a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v-1.72a2 2 0 0 0-1-1.7C6.63 14.68 5 13.07 5 10z" />
            <line x1="9" y1="22" x2="15" y2="22" />
          </svg>
          insights
        </HeaderIconLink>
        <HeaderIconLink to="/">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          home
        </HeaderIconLink>

        {isAuthenticated ? (
          <ProfileImage size={36} />
        ) : (
          <HeaderIconButton onClick={handleLogin}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
              <polyline points="10 17 15 12 10 7"/>
              <line x1="15" y1="12" x2="3" y2="12"/>
            </svg>
            login
          </HeaderIconButton>
        )}
        <NotificationsBell />
      </HomeHeader>

      {/* Mobile Menu Toggle Button */}
      <MobileMenuToggle
        onClick={() => setMenuOpen(!menuOpen)}
        data-mobile-toggle="true"
        aria-label="Toggle menu"
      >
        <MobileMenuIcon isOpen={menuOpen}>
          <span></span>
          <span></span>
          <span></span>
        </MobileMenuIcon>
      </MobileMenuToggle>

      {/* Mobile Menu Dropdown */}
        <MobileMenuDropdown isOpen={menuOpen} ref={mobileMenuRef}>
        
        <MobileMenuLink to="/team">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>
          team
        </MobileMenuLink>
        {!location.pathname.startsWith('/mission') && (
          <MobileMenuLink to="/mission" onClick={() => setMenuOpen(false)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
              <line x1="4" y1="22" x2="4" y2="15" />
            </svg>
            mission
          </MobileMenuLink>
        )}
        <MobileMenuLink to="/insights">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="2" x2="12" y2="6" />
            <path d="M5 10a7 7 0 0 1 14 0c0 3.07-1.63 4.68-3 5.58a2 2 0 0 0-1 1.7V19a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v-1.72a2 2 0 0 0-1-1.7C6.63 14.68 5 13.07 5 10z" />
            <line x1="9" y1="22" x2="15" y2="22" />
          </svg>
          insights
        </MobileMenuLink>

        {isAuthenticated ? (
          <>
            <MobileMenuLink to="/desktop">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2"/>
                <path d="M16 12h.01"/>
                <path d="M2 10h20"/>
              </svg>
              desktop
            </MobileMenuLink>
            {/* ORG-USAGE-2026: same billing.view gate as the header nav link —
                fail-open (guests / legacy accounts unaffected), so a member the
                admin restricted doesn't get a mobile-menu path to the access wall. */}
            {canSeeWallet && (
              <MobileMenuLink to="/wallet">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="M16 12h.01" />
                  <path d="M2 10h20" />
                </svg>
                wallet
              </MobileMenuLink>
            )}
            {/* ORG-USAGE-2026: same activity.view gate as the header nav link —
                fail-open. Menu order convention: activity sits between wallet
                and profile. */}
            {canSeeActivity && (
              <MobileMenuLink to="/activity">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 3v18h18" />
                  <path d="M7 15l4-4 3 3 5-6" />
                </svg>
                activity
              </MobileMenuLink>
            )}
            {!location.pathname.startsWith('/profile') && (
              <MobileMenuLink to="/profile">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                profile
              </MobileMenuLink>
            )}
            <MobileMenuLink to="/settings">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
              settings
            </MobileMenuLink>
            <MobileNotifications closeMenu={() => setMenuOpen(false)} />
            <MobileMenuButton onClick={() => logout()} style={{ color: '#ff4d4f' }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              logout
            </MobileMenuButton>
          </>
        ) : (
          <MobileMenuButton onClick={handleLogin}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
              <polyline points="10 17 15 12 10 7"/>
              <line x1="15" y1="12" x2="3" y2="12"/>
            </svg>
            login
          </MobileMenuButton>
        )}
      </MobileMenuDropdown>

      {/* ── Hero ── */}
      <MissionHero>
        <HeroTitle>expanding expert capacity for law and finance</HeroTitle>
        <HeroSubtitle>
          great specialist work shouldn't be bottlenecked by headcount — we're changing that
        </HeroSubtitle>
      </MissionHero>

      {/* ── Opening Quote ── */}
      <QuoteContainer>
        <QuoteText>
          specialist capacity isn't out of reach because talent is scarce — it's out of reach because research, drafting, intake, and coordination still consume the hours experts should spend on judgment
        </QuoteText>
      </QuoteContainer>

      {/* ── Story Block 1: The Problem ── */}

      <StoryFeatureBlock
        label="01 / the problem"
        title="expert capacity is too scarce for the work that needs doing."
        text="Law and finance teams are buried in research, drafting, workpaper support, compliance checks, client communication, and handoffs. It isn't because people don't care — the overhead of coordination and production has not scaled with demand. Specialists spend their days on work AI can prepare, so capacity stays too low for the work that actually requires them."
        imageSrc="/images/mission-problem.webp"
        imageAlt="The expert capacity problem"
      />

      {/* ── Story Block 2: The Gap ── */}
      <StoryFeatureBlock
        label="02 / the gap"
        title="ai can close the gap — through the teams already doing the work."
        text="In law: case intake, research, and drafting. In finance: tax research memos, workpaper support, KYC/AML review, investment memos, and quarterly letters. The teams who want to move faster already exist — for years the technology wasn't there to let them compound without hiring a new person for every new workflow."
        imageSrc="/images/mission-gap.webp"
        imageAlt="The capacity gap across verticals"
        reversed
      />

      {/* ── Story Block 3: Our Approach ── */}
      <StoryFeatureBlock
        label="03 / our approach"
        title="we work through your specialists — not around them."
        text="prest0 doesn't replace lawyers or analysts. We give law and finance teams AI employees that handle the research, drafting, coordination, and execution layers — so human experts can take more matters, clear more reviews, ship more memos, and still raise the quality bar. Isolated per-team environments, persistent memory, and real tool access make the work durable, not a chat demo."
        imageSrc="/images/mission-approach.webp"
        imageAlt="Our approach to AI employees"
      />

      {/* ── Story Block 4: The Platform ── */}
      <StoryFeatureBlock
        label="04 / the result"
        title="more work shipped. better outcomes. lower cost per unit of output."
        text="When a law team can intake and draft in minutes, and a finance team can turn tax research into memos, keep workpapers current, and clear KYC/AML review without losing the thread — those teams say yes more often. That's how capacity expands: not by bypassing experts, but by making it possible for experts to do the judgment only they can do."
        imageSrc="/images/mission-platform.webp"
        imageAlt="The result of our approach"
        reversed
      />

      {/* ── Closing Quote ── */}
      <ClosingQuoteContainer>
        <ClosingQuoteText>
          every specialist deserves time for the work that actually needs them —
          prest0 gives law and finance teams the AI employees to make that true
        </ClosingQuoteText>
      </ClosingQuoteContainer>

    </PageContainer>

    {/* ── CTA — outside PageContainer, full-bleed, exact home page style ── */}
    <section style={{
      width: '100%',
      padding: '7rem 2rem',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      background: 'linear-gradient(135deg, var(--brand-black) 0%, #2a2a2a 100%)',
    }}>
        <div style={{ maxWidth: '1000px', width: '100%', textAlign: 'center' }}>
          <DarkCTATitle>
            join <DarkCTAGradientText>prest</DarkCTAGradientText><DarkCTALogo src={BRAND_MARK_ON_DARK_SRC} alt="0" />
          </DarkCTATitle>
          <button
            onClick={handleJoinForFree}
            style={{
              background: 'linear-gradient(135deg, var(--brand-gold-alt), #B8924C)',
              color: '#fff',
              border: 'none',
              borderRadius: '999px',
              padding: '1.15rem 3rem',
              fontSize: '1.2rem',
              fontWeight: 700,
              cursor: 'pointer',
              letterSpacing: '0.02em',
              boxShadow: '0 8px 28px rgba(200, 167, 94, 0.35)',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-3px)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 12px 36px rgba(200, 167, 94, 0.45)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 8px 28px rgba(200, 167, 94, 0.35)'; }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 22, height: 22 }}>
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            book a meeting
          </button>
        </div>
    </section>

    <Modal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        title="book a live demo"
        size="xlarge"
        maxWidth="1100px"
        footer={null}
        noScroll={true}
      >
        <div style={{ height: 'calc(90vh - 80px)', minHeight: '600px', width: '100%', borderRadius: '0 0 12px 12px', overflow: 'hidden' }}>
          <iframe
            src="https://calendar.google.com/calendar/appointments/schedules/AcZssZ2bVpuAMuVJh3Xikmu1Bi9AuBaaHxB5a-HeOlE8xm3l2ehjgrdvG5QQEqmrLDKsF1Vgbncg2BIK?gv=true"
            style={{ border: 'none', width: '100%', height: '100%' }}
          ></iframe>
        </div>
    </Modal>

    <GlobalFooter />
    </>
  );
};

export default Mission;
